/**
 * Context for "ResizeObserver loop completed with undelivered notifications" in the Studio (bug 538890).
 *
 * The browser raises that error with no stack, no element and no observer, so the relayed log can only say that it
 * happened. These diagnostics record what surrounded it — which observers delivered in the failing frame, the last
 * input, the display and scrollbar setup, extension markers and long animation frames — and put that, as JSON, in
 * the stack of the error the GlobalErrorTracker already sends, so it reaches the logs as `errorStack` without an API
 * change.
 *
 * Nothing here may change how the app behaves: it is installed on Studio pages only, every recording step swallows
 * its own failures, an observer's callback is always called exactly as the browser would have called it, and every
 * other error is relayed as before. Only ids, widget types, class names and sizes are recorded — never text.
 *
 * TODO(538890): remove once the cause of the Studio loop errors is known.
 */

const LOOP_MESSAGE = 'ResizeObserver loop';
const STUDIO_PATH_PREFIX = '/studio';

/** The first error of a page and then at most one every 10 s, 5 per page: the rest are relayed unchanged. */
const MAX_DIAGNOSED_PER_PAGE = 5;
const DIAGNOSE_INTERVAL_MS = 10_000;

const MAX_DELIVERIES = 60;
const FRAME_WINDOW_MS = 100;
const SITE_WINDOW_MS = 1000;
const MAX_LONG_FRAMES = 5;
const MAX_JSON_LENGTH = 6000;

type Delivery = { at: number; site: string; count: number; targets: string[] };
type LastInput = { type: string; at: number; x?: number; y?: number };
type LongFrame = { at: number; duration: number; blocking?: number; scripts: Record<string, unknown>[] };

/** The `long-animation-frame` entry shape, which the TypeScript DOM library does not describe yet. */
type LongAnimationFrameEntry = PerformanceEntry & {
  blockingDuration?: number;
  scripts?: {
    duration: number;
    invoker?: string;
    invokerType?: string;
    sourceFunctionName?: string;
    sourceURL?: string;
  }[];
};

const state = {
  diagnosed: 0,
  installed: false,
  lastDiagnosedAt: Number.NEGATIVE_INFINITY,
  lastInput: null as LastInput | null,
  longFrames: [] as LongFrame[],
  loopErrors: 0,
  deliveries: [] as Delivery[]
};

const shortenUrl = (url: string) => url.replace(/^[a-z-]+:\/\/[^/]+/i, '').replace(/\?[^:)\s]*/g, '');

/** Where an observer was created: the first two stack frames outside this file, without origin or query. */
const creationSite = (): string => {
  const frames = (new Error().stack ?? '').split('\n').slice(1);
  return frames
    .filter(frame => !frame.includes('resizeObserverDiagnostics'))
    .slice(0, 2)
    .map(frame => shortenUrl(frame.trim().replace(/^at /, '')).slice(0, 120))
    .join(' < ');
};

/** A text-free description of an element: tag, test id, first class and the stage widget it sits in. */
const describeElement = (node: Element | null | undefined): string => {
  if (!node || typeof node.getAttribute !== 'function') return '';
  const testId = node.getAttribute('data-testid');
  const widget = node.closest?.('[data-testid^="stage-item-"]')?.getAttribute('data-testid');
  const firstClass = typeof node.className === 'string' ? node.className.split(' ')[0] : '';
  return [
    node.tagName.toLowerCase(),
    testId && `#${testId}`,
    firstClass && `.${firstClass}`,
    widget && widget !== testId && `in ${widget}`
  ]
    .filter(Boolean)
    .join(' ')
    .slice(0, 160);
};

const record = (site: string, entries: ResizeObserverEntry[]) => {
  try {
    state.deliveries.push({
      at: performance.now(),
      site,
      count: entries.length,
      targets: entries
        .slice(0, 3)
        .map(
          entry =>
            `${describeElement(entry.target)} ${Math.round(entry.contentRect.width)}x${Math.round(
              entry.contentRect.height
            )}`
        )
    });
    if (state.deliveries.length > MAX_DELIVERIES) state.deliveries.shift();
  } catch {
    // Diagnostics never interfere with the observer.
  }
};

const wrapResizeObserver = () => {
  const NativeResizeObserver = window.ResizeObserver;

  class DiagnosedResizeObserver extends NativeResizeObserver {
    constructor(callback: ResizeObserverCallback) {
      // A non-function must still make the native constructor throw its own TypeError.
      if (typeof callback !== 'function') {
        super(callback);
        return;
      }
      const site = creationSite();
      super(function (this: ResizeObserver, entries, observer) {
        record(site, entries);
        return callback.call(this, entries, observer);
      });
    }
  }

  window.ResizeObserver = DiagnosedResizeObserver;
};

const listenToInput = () => {
  const remember = (event: Event) => {
    const pointer = event as Partial<PointerEvent>;
    state.lastInput = { type: event.type, at: performance.now(), x: pointer.clientX, y: pointer.clientY };
  };
  const options = { capture: true, passive: true };
  ['pointerdown', 'pointermove', 'pointerup', 'wheel', 'keydown', 'resize'].forEach(type =>
    window.addEventListener(type, remember, options)
  );
};

const observeLongFrames = () => {
  if (typeof PerformanceObserver === 'undefined') return;
  if (!PerformanceObserver.supportedEntryTypes?.includes('long-animation-frame')) return;
  new PerformanceObserver(list => {
    try {
      for (const entry of list.getEntries() as LongAnimationFrameEntry[]) {
        state.longFrames.push({
          at: Math.round(entry.startTime),
          duration: Math.round(entry.duration),
          blocking: entry.blockingDuration && Math.round(entry.blockingDuration),
          scripts: (entry.scripts ?? []).slice(0, 3).map(script => ({
            duration: Math.round(script.duration),
            invoker: script.invoker?.slice(0, 80),
            invokerType: script.invokerType,
            fn: script.sourceFunctionName?.slice(0, 60),
            source: script.sourceURL && shortenUrl(script.sourceURL).slice(0, 120)
          }))
        });
        if (state.longFrames.length > MAX_LONG_FRAMES) state.longFrames.shift();
      }
    } catch {
      // Diagnostics never interfere with the page.
    }
  }).observe({ type: 'long-animation-frame', buffered: false });
};

/** Installs the diagnostics on Studio pages. Safe to call more than once; never throws. */
export const installResizeObserverDiagnostics = () => {
  if (state.installed || typeof window === 'undefined' || typeof window.ResizeObserver !== 'function') return;
  if (!window.location.pathname.startsWith(STUDIO_PATH_PREFIX)) return;
  state.installed = true;
  try {
    wrapResizeObserver();
    listenToInput();
    observeLongFrames();
  } catch {
    // A failed install leaves the native observer and the default relay in place.
  }
};

const collect = (now: number) => {
  const frame = state.deliveries.filter(delivery => now - delivery.at <= FRAME_WINDOW_MS).slice(-10);
  const sites: Record<string, number> = {};
  state.deliveries
    .filter(delivery => now - delivery.at <= SITE_WINDOW_MS)
    .forEach(delivery => {
      sites[delivery.site] = (sites[delivery.site] ?? 0) + 1;
    });
  const input = state.lastInput;
  const root = document.documentElement;
  const visualViewport = window.visualViewport;

  return {
    v: 1,
    loopErrors: state.loopErrors,
    msSinceLoad: Math.round(now),
    frame: frame.map(delivery => ({
      msAgo: Math.round(now - delivery.at),
      site: delivery.site,
      count: delivery.count,
      targets: delivery.targets
    })),
    sites,
    input: input && {
      type: input.type,
      msAgo: Math.round(now - input.at),
      under:
        input.x === undefined || input.y === undefined
          ? undefined
          : describeElement(document.elementFromPoint(input.x, input.y))
    },
    focused: describeElement(document.activeElement),
    openDropdowns: document.querySelectorAll('[class*="-dropdown"]').length,
    env: {
      dpr: window.devicePixelRatio,
      zoom: visualViewport?.scale,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      screen: `${window.screen?.width}x${window.screen?.height}`,
      scrollbar: window.innerWidth - root.clientWidth,
      fonts: document.fonts?.status,
      visibility: document.visibilityState,
      webdriver: navigator.webdriver,
      reducedMotion: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    },
    ext: {
      grammarly:
        document.body?.hasAttribute('data-gr-ext-installed') ||
        Boolean(document.querySelector('grammarly-desktop-integration'))
    },
    longFrames: state.longFrames.filter(longFrame => now - longFrame.at <= SITE_WINDOW_MS + longFrame.duration)
  };
};

/**
 * Adds the diagnostics to the stack of the error relayed for a ResizeObserver loop error, when installed and not
 * rate limited. Returns the same error, untouched, in every other case.
 */
export const withResizeObserverDiagnostics = (error: Error): Error => {
  if (!state.installed || !error.message.includes(LOOP_MESSAGE)) return error;
  try {
    state.loopErrors += 1;
    const now = performance.now();
    if (state.diagnosed >= MAX_DIAGNOSED_PER_PAGE || now - state.lastDiagnosedAt < DIAGNOSE_INTERVAL_MS) return error;
    state.diagnosed += 1;
    state.lastDiagnosedAt = now;
    const json = JSON.stringify(collect(now)).slice(0, MAX_JSON_LENGTH);
    error.stack = `${error.name}: ${error.message}\nresizeObserverDiagnostics ${json}`;
  } catch {
    // The error is relayed as it would have been without diagnostics.
  }
  return error;
};
